import { useEffect } from 'react'
import { useNavigate } from 'react-router-dom'
import { usePricebooks } from '@/hooks/use-api'
import { AlertCircle, Loader2 } from 'lucide-react'

/**
 * `/pricebooks` — sends the user to the one price list.
 *
 * GRM used to present a list of pricebooks with create, clone, edit and delete
 * actions. In practice only ever one of them held any prices: the two others
 * were empty, no screen anywhere could select a pricebook for a deal, and every
 * read path in the application hardcoded `is_standard = true`. It was an
 * abstraction that cost maintenance and governed nothing.
 *
 * There is now a single price list, so a list page showing one row is just an
 * extra click. This redirects straight to it and the navigation entry is
 * labelled "Price List".
 *
 * The pricebook TABLE is unchanged — prices still live in
 * sales.pricebook_entries keyed (pricebook, product, currency) — so if genuinely
 * separate price lists are ever wanted (regional or partner pricing), the model
 * still supports them. This removes the unused UI, not the capability.
 */
export default function PriceListRedirect() {
  const navigate = useNavigate()
  const { data, isLoading, error } = usePricebooks({ is_standard: true, limit: 1 })
  const standard = data?.data?.[0]

  useEffect(() => {
    if (standard?.id) {
      // `replace` so Back doesn't bounce the user through this redirect again.
      navigate(`/pricebooks/${standard.id}/prices`, { replace: true })
    }
  }, [standard?.id, navigate])

  if (isLoading || standard?.id) {
    return (
      <div
        className="flex items-center justify-center gap-2 py-16 text-muted-foreground"
        role="status"
        aria-live="polite"
      >
        <Loader2 className="h-4 w-4 animate-spin" aria-hidden="true" />
        Opening the price list…
      </div>
    )
  }

  // No standard price list. Every product price hangs off it, so this is worth
  // explaining rather than showing an empty screen.
  return (
    <div className="max-w-xl mx-auto py-16 text-center space-y-3" role="alert">
      <AlertCircle className="h-8 w-8 mx-auto text-warning" aria-hidden="true" />
      <h1 className="text-xl font-semibold">No price list found</h1>
      <p className="text-sm text-muted-foreground">
        {error
          ? 'The price list could not be loaded. Please refresh, and contact support if it keeps happening.'
          : 'GRM expects one standard price list, and none exists. Product prices cannot be read or set until it is restored — please contact support.'}
      </p>
    </div>
  )
}
